/**
 * App.js
 * ───────
 * Routing layout for BeXyl IPM DSS.
 *
 * Public routes (no login required):
 *   /          → LandingPage
 *   /search    → WizardPage (search + results)
 *
 * Admin routes (login required at /admin):
 *   /admin/*   → AdminLayout + sub-pages
 *   Login page shown automatically when /admin is visited unauthenticated
 *
 * TOGGLE: To restore user login requirement, wrap the public routes block
 * in <RequireAuth> (see commented instructions below).
 */

import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "./context/AuthContext";

import LandingPage          from "./pages/user/LandingPage";
import WizardPage           from "./pages/user/WizardPage";
import InfoPage             from "./pages/user/InfoPage";
import SchemeCatalogPage    from "./pages/user/SchemeCatalogPage";
import LoginPage            from "./pages/user/LoginPage";
import AdminLayout       from "./pages/admin/AdminLayout";
import DashboardPage     from "./pages/admin/DashboardPage";
import UsersPage         from "./pages/admin/UsersPage";
import PrivilegesPage    from "./pages/admin/PrivilegesPage";
import SchemesAdminPage  from "./pages/admin/SchemesAdminPage";
import DocumentsAdminPage from "./pages/admin/DocumentsAdminPage";
import MonitorPage       from "./pages/admin/MonitorPage";

// ── Questionnaire popup ────────────────────────────────────────────────────────

function QuestionnairePopup({ onClose }) {
  return (
    <>
      <style>{`
        @keyframes qp-fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes qp-slideUp {
          from { opacity:0; transform:translateY(24px) scale(.97) }
          to   { opacity:1; transform:translateY(0)   scale(1)    }
        }
        .qp-overlay {
          position:fixed; inset:0; z-index:2000;
          background:rgba(30,50,10,.52); backdrop-filter:blur(4px);
          display:flex; align-items:center; justify-content:center;
          padding:20px; animation:qp-fadeIn .3s ease;
        }
        .qp-card {
          background:#fff; border-radius:20px; padding:36px 32px 28px;
          max-width:440px; width:100%; position:relative;
          box-shadow:0 24px 64px rgba(30,50,10,.28);
          border:1.5px solid #D4D980;
          animation:qp-slideUp .35s cubic-bezier(.2,.7,.2,1);
          font-family:'Nunito',system-ui,sans-serif;
        }
        .qp-close {
          position:absolute; top:14px; right:14px;
          background:none; border:none; cursor:pointer;
          color:#bbb; padding:6px; border-radius:8px;
          transition:background .2s,color .2s; display:grid; place-items:center;
        }
        .qp-close:hover { background:#f3f3f3; color:#555; }
        .qp-icon {
          width:52px; height:52px; border-radius:15px;
          background:linear-gradient(135deg,#5d8f22,#3d6213);
          display:grid; place-items:center; color:#fff;
          margin-bottom:16px; box-shadow:0 8px 22px rgba(75,117,24,.30);
        }
        .qp-title {
          font-size:20px; font-weight:900; color:#3d6213;
          margin:0 0 10px; line-height:1.2;
        }
        .qp-body {
          font-size:14px; color:#775F46; line-height:1.72; margin:0 0 22px;
        }
        .qp-btn {
          display:flex; align-items:center; justify-content:center; gap:10px;
          width:100%; padding:14px; border-radius:12px; border:none;
          background:linear-gradient(110deg,#D4D980 0%,#edf07e 40%,#D4D980 60%,#c4ca60 100%);
          background-size:250% auto; color:#4a5500;
          font-size:14.5px; font-weight:800; cursor:pointer;
          font-family:'Nunito',system-ui,sans-serif;
          box-shadow:0 4px 20px rgba(212,217,128,.55);
          transition:background-position .55s ease,box-shadow .25s,transform .2s;
          text-decoration:none;
        }
        .qp-btn:hover {
          background-position:right center;
          box-shadow:0 10px 34px rgba(212,217,128,.75);
          transform:translateY(-2px);
        }
        .qp-skip {
          display:block; text-align:center; margin-top:12px;
          font-size:12.5px; color:#bbb; cursor:pointer;
          font-family:'Nunito',system-ui,sans-serif;
          background:none; border:none; transition:color .2s;
          width:100%;
        }
        .qp-skip:hover { color:#775F46; }
      `}</style>
      <div className="qp-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="qp-card" role="dialog" aria-modal="true" aria-labelledby="qp-title">
          <button className="qp-close" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>

          <div className="qp-icon">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
              <rect x="9" y="3" width="6" height="4" rx="1"/>
              <line x1="9" y1="12" x2="15" y2="12"/>
              <line x1="9" y1="16" x2="13" y2="16"/>
            </svg>
          </div>

          <h2 className="qp-title" id="qp-title">Help us improve the DSS</h2>
          <p className="qp-body">
            Your feedback matters! Take a minute to fill in our short questionnaire and help us
            make the BeXyl Decision Support System better for all users across Europe.
          </p>

          <a
            className="qp-btn"
            href="https://forms.gle/32ixMXncRyTXkWyo6"
            target="_blank"
            rel="noreferrer"
            onClick={onClose}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
            Take the questionnaire
          </a>
          <button className="qp-skip" onClick={onClose}>Maybe later</button>
        </div>
      </div>
    </>
  );
}

// ── Guards ─────────────────────────────────────────────────────────────────────

function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user)                 return <Navigate to="/admin/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function RequireCatalog({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role !== "admin" && user.role !== "risk_manager") return <Navigate to="/" replace />;
  return children;
}

function Spinner() {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center",
      justifyContent: "center", color: "var(--bx-olive)", fontSize: 14,
      fontFamily: "'Nunito', sans-serif" }}>
      Loading…
    </div>
  );
}

// ── App inner (needs to be inside BrowserRouter to use useLocation) ────────────

function AppInner() {
  const location = useLocation();
  const isPublicRoute = !location.pathname.startsWith("/admin");

  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    if (!isPublicRoute) return;
    const t = setTimeout(() => setShowPopup(true), 3000);
    return () => clearTimeout(t);
  }, []); // intentionally empty — run once on mount only

  return (
    <>
      <Routes>
        {/* ── Public user routes — no login needed ── */}
        {/* TOGGLE: wrap with <RequireAuth> to enforce user login */}
        <Route path="/"       element={<LandingPage />} />
        <Route path="/search" element={<WizardPage />} />
        <Route path="/info"   element={<InfoPage />} />

        {/* ── Scheme catalog — risk_manager and admin only ── */}
        <Route path="/catalog" element={
          <RequireCatalog>
            <SchemeCatalogPage />
          </RequireCatalog>
        } />

        {/* ── Admin login (separate URL from public login) ── */}
        <Route path="/admin/login" element={<LoginPage adminMode />} />

        {/* ── Admin panel — requires admin role ── */}
        <Route path="/admin/*" element={
          <RequireAdmin>
            <AdminLayout>
              <Routes>
                <Route path="/"           element={<DashboardPage />} />
                <Route path="/users"      element={<UsersPage />} />
                <Route path="/privileges" element={<PrivilegesPage />} />
                <Route path="/schemes"    element={<SchemesAdminPage />} />
                <Route path="/documents"  element={<DocumentsAdminPage />} />
                <Route path="/monitor"    element={<MonitorPage />} />
              </Routes>
            </AdminLayout>
          </RequireAdmin>
        } />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* {showPopup && isPublicRoute && (
        <QuestionnairePopup onClose={() => setShowPopup(false)} />
      )} */}
    </>
  );
}

// ── App ─────────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  );
}
